import styles from './styles.module.css'

import React, { useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import { getUser } from '@disco/state-management/selectors/user'
import { pushUINotification } from '@disco/utils/notifications'
import { validateEmail } from '@disco/utils/validation'

import { Brief } from 'types/brief'

import { RecipientInput } from 'components/common/recipient-input'
import {
  Box,
  Button as DsButton,
  Dialog,
  Flex as DsFlex,
  Link,
  Text,
  TextArea,
  TextField,
} from 'components/ds'
import Button from 'components/ui/button'
import InlineError from 'components/ui/inline-error'

import { useSelector } from 'hooks/use-redux'

import { useShareBrief } from './hooks'

interface RecipientTag {
  id: string
  email: string
  contactId?: number
  isValid: boolean
}

export interface EmailTabState {
  recipients: RecipientTag[]
  senderName: string
  replyToAddress: string
  replyToAddressError: string | null
  subject: string
  message: string
}

interface EmailTabProps {
  brief: Brief
  initialRecipients?: string[]
  allRecipients?: Array<{ email: string; contactId?: number }>
  onManageAccess: () => void
  handleReopenBrief: () => Promise<void>
  onClose?: () => void
  persistedStateRef: React.MutableRefObject<EmailTabState | null>
  briefWorkflowV2Enabled?: boolean
}

interface ShareBriefData {
  recipients: string[]
  replyToAddress: string
  senderName: string
  subject: string
  message: string
}

export const EmailTab: React.FC<EmailTabProps> = ({
  brief,
  initialRecipients = [],
  allRecipients,
  onManageAccess,
  handleReopenBrief,
  onClose,
  persistedStateRef,
  briefWorkflowV2Enabled = false,
}) => {
  const { shareBrief } = useShareBrief()
  const user = useSelector(getUser)

  const initialState = React.useMemo<EmailTabState>(() => {
    if (persistedStateRef.current) {
      return persistedStateRef.current
    }

    const recipientTags: RecipientTag[] = initialRecipients.map(email => ({
      id: `${Date.now()}-${Math.random()}`,
      email: email.trim(),
      isValid: validateEmail(email.trim()),
    }))

    const defaultSenderName =
      brief.sender_name ||
      (user.first_name && user.last_name
        ? `${user.first_name} ${user.last_name}`
        : '')

    const defaultReplyTo = brief.sender_email || user.email || ''

    return {
      recipients: recipientTags,
      senderName: defaultSenderName,
      replyToAddress: defaultReplyTo,
      replyToAddressError: null,
      subject: brief.name || 'Brief',
      message: '',
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [recipients, setRecipients] = useState<RecipientTag[]>(
    initialState.recipients,
  )
  const [senderName, setSenderName] = useState(initialState.senderName)
  const [replyToAddress, setReplyToAddress] = useState(
    initialState.replyToAddress,
  )
  const [replyToAddressError, setReplyToAddressError] = useState<string | null>(
    initialState.replyToAddressError,
  )
  const [isReplyToAddressFocused, setIsReplyToAddressFocused] = useState(false)
  const [subject, setSubject] = useState(initialState.subject)
  const [message, setMessage] = useState(initialState.message)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  React.useEffect(() => {
    // eslint-disable-next-line no-param-reassign
    persistedStateRef.current = {
      recipients,
      senderName,
      replyToAddress,
      replyToAddressError,
      subject,
      message,
    }
  }, [
    recipients,
    senderName,
    replyToAddress,
    replyToAddressError,
    subject,
    message,
    persistedStateRef,
  ])

  const addRecipient = (email?: string, _name?: string, contactId?: number) => {
    const trimmedEmail = email?.trim() || ''
    if (!trimmedEmail) return

    const isValid = validateEmail(trimmedEmail)

    // Dedupe inside the updater so a burst of mailing-list adds sees each
    // previous iteration's state.
    setRecipients(prev => {
      const isDuplicate = prev.some(
        r => r.email.toLowerCase() === trimmedEmail.toLowerCase(),
      )
      if (isDuplicate) return prev
      return [
        ...prev,
        {
          id: `${Date.now()}-${Math.random()}`,
          email: trimmedEmail,
          contactId,
          isValid,
        },
      ]
    })
  }

  const removeRecipient = (id: string) => {
    setRecipients(prev => prev.filter(r => r.id !== id))
  }

  const handleReplyToAddressChange = (value: string) => {
    setReplyToAddress(value)

    if (value.trim()) {
      const isValid = validateEmail(value.trim())
      setReplyToAddressError(
        isValid ? null : 'Please enter a valid email address',
      )
    } else {
      setReplyToAddressError(null)
    }
  }

  const validRecipients = recipients.filter(r => r.isValid)

  const isBriefClosed =
    briefWorkflowV2Enabled &&
    brief?.submission_status === 'closed' &&
    brief?.publishing_status !== 'draft'

  const canSend =
    validRecipients.length > 0 && subject.trim() && !replyToAddressError

  const recipientCount = validRecipients.length

  const handleSend = async () => {
    if (!canSend) return

    setIsLoading(true)
    setError(null)

    if (isBriefClosed) {
      await handleReopenBrief()
      mixpanel.sendPureEvent('Brief reopened', {
        Context: 'Briefs',
        brief_id: brief.id,
      })
    }

    try {
      const shareData: ShareBriefData = {
        recipients: validRecipients.map(r => r.email),
        replyToAddress: replyToAddress.trim(),
        senderName: senderName.trim(),
        subject: subject.trim(),
        message: message.trim(),
      }

      const result = await shareBrief(brief.id, shareData)
      const customizedFields: string[] = []

      if (
        subject.trim() !== '' &&
        subject.trim() !== 'Brief' &&
        subject.trim() !== brief.name
      ) {
        customizedFields.push('subject')
      }

      if (message.trim() !== '') {
        customizedFields.push('message')
      }

      const defaultReplyTo = brief.sender_email || user.email || ''

      if (
        replyToAddress.trim() !== '' &&
        replyToAddress.trim() !== defaultReplyTo
      ) {
        customizedFields.push('reply_to')
      }

      if (result.success) {
        mixpanel.sendPureEvent('Brief shared', {
          Context: 'Briefs',
          brief_id: brief.id,
          recipient_count: recipientCount,
          customized_fields: customizedFields,
        })
        pushUINotification({
          message: `Brief shared with ${validRecipients.length} recipient${
            validRecipients.length === 1 ? '' : 's'
          }!`,
          level: 'success',
        })

        onClose?.()
      } else {
        throw new Error(result.error || 'Failed to share brief')
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to share brief. Please try again.'
      setError(errorMessage)

      pushUINotification({
        message: 'Failed to share brief. Please try again.',
        level: 'error',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const sendButtonText =
    recipientCount > 0
      ? `Send to ${recipientCount} recipient${recipientCount === 1 ? '' : 's'}`
      : 'Send'

  const handleAddAllRecipients = () => {
    allRecipients?.forEach(({ email, contactId }) => {
      addRecipient(email, undefined, contactId)
    })
  }

  const allRecipientsButton = (
    <DsButton
      mt="1"
      ml="2"
      variant="ghost"
      onClick={handleAddAllRecipients}
      className={styles.focusable}
    >
      <Text size="2" color="blue">
        + Add existing recipients
      </Text>
    </DsButton>
  )

  return (
    <>
      <Box>
        <Box mb="3">
          <RecipientInput
            recipients={recipients}
            onAdd={addRecipient}
            onRemove={removeRecipient}
            aria-label="Brief share recipients"
          />
          {!!allRecipients?.length && allRecipientsButton}
        </Box>
      </Box>

      <Box className={styles.scrollArea}>
        <Box mb="3">
          <Text size="2" weight="medium" as="label" htmlFor="reply-to-address">
            Reply to address
          </Text>
          <TextField.Root
            id="reply-to-address"
            size="2"
            type="email"
            placeholder="you@company.com"
            value={replyToAddress}
            onChange={e => handleReplyToAddressChange(e.target.value)}
            onFocus={() => setIsReplyToAddressFocused(true)}
            onBlur={() => setIsReplyToAddressFocused(false)}
            aria-describedby="reply-to-help"
            mt="1"
            color={
              replyToAddressError && !isReplyToAddressFocused
                ? 'red'
                : undefined
            }
          />
          {replyToAddressError && !isReplyToAddressFocused && (
            <InlineError error={replyToAddressError} />
          )}
        </Box>

        <Box mb="3">
          <Text size="2" weight="medium" as="label" htmlFor="sender-name">
            Sender display name
          </Text>
          <TextField.Root
            id="sender-name"
            size="2"
            type="text"
            placeholder="Your Name"
            value={senderName}
            onChange={e => setSenderName(e.target.value)}
            aria-describedby="sender-name-help"
            mt="1"
          />
        </Box>

        <Box mb="3">
          <Text size="2" weight="medium" as="label" htmlFor="email-subject">
            Subject
          </Text>
          <TextField.Root
            id="email-subject"
            size="2"
            placeholder="Enter email subject"
            value={subject}
            onChange={e => setSubject(e.target.value)}
            required
            aria-required="true"
            mt="1"
          />
        </Box>

        <Box mb="3">
          <Text size="2" weight="medium" as="label" htmlFor="email-message">
            Message
          </Text>
          <TextArea
            id="email-message"
            size="2"
            placeholder="Hi everyone, check out this new brief!"
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={6}
            aria-describedby="message-help"
            mt="1"
          />
        </Box>

        {(brief.status === 'sent' || brief.sent_status) && (
          <Box mb="4">
            <Link
              href="#"
              size="2"
              color="blue"
              onClick={onManageAccess}
              className={styles.focusable}
            >
              Manage brief access
            </Link>
          </Box>
        )}

        {error && (
          <Box mb="3">
            <Text size="2" color="red">
              {error}
            </Text>
          </Box>
        )}
      </Box>

      <DsFlex gap="3" justify="end">
        <Dialog.Close>
          <DsButton
            variant="soft"
            color="gray"
            size="2"
            className={styles.focusable}
          >
            Cancel
          </DsButton>
        </Dialog.Close>

        <Button
          appearance="primary"
          onClick={handleSend}
          disabled={!canSend || isLoading}
          caption={sendButtonText}
          progressCaption="Sending"
          progressIndicator={isLoading}
          className={styles.focusable}
        />
      </DsFlex>
    </>
  )
}
